import React, { useEffect, useState } from 'react';
import useTranslate from '../../../../Hooks/useTranslate';
import useAPI from '../../../../api';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  Fade,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { openSnackbarAdviseAtom, userBranchesAtom } from '../../../../Atoms';
import SectionPage from '../../../../Components/Paper/SectionPage';
import { FullSizeCenteredFlexBox } from '../../../../Components/FlexBox';
import capitalize from '../../../../Helpers/StringsFormatting/capitalize';
import BackButton from '../BackButton';
import { createNewDate, culture, options } from '../../../../Helpers/dates';
import FiltersConfiguration from './Pages/FiltersConfiguration';
import SimulationSummaries from './Pages/SimulationSummaries';

const SimulationsHistory = function SimulationsHistory({
  show = false,
  close = () => {},
  onSave,
  data,
  tabs = [],
  loadStagedImports = () => {},
}) {
  const API = useAPI();
  const translate = useTranslate();
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const userBranches = useAtomValue(userBranchesAtom);

  const [simulations, setSimulations] = useState([]);
  const [filtersConfigurationPage, setFiltersConfigurationPage] =
    useState(null);
  const [simulationSummariesPage, setSimulationSummariesPage] = useState(null);

  const loadSimulations = () => {
    API.getUncommittedWaveSimulations(
      data?.branchId,
      (simulations) => setSimulations(simulations),
      (error) => {
        openSnackbar({
          message: translate('ERROR_LOADING_SIMULATIONS'),
          severity: 'error',
        });
      }
    );
  };

  useEffect(() => {
    if (!show || filtersConfigurationPage || simulationSummariesPage) return;

    loadSimulations();
  }, [show, filtersConfigurationPage, simulationSummariesPage]);

  const handleShowFiltersConfiguration = ({ id: simulationId }) => {
    if (!simulationId) {
      setFiltersConfigurationPage({
        branchId: data?.branchId,
        filterParameters: null,
      });
      return;
    }
  };

  const handleRunWaveSimulation = ({ branchId, schema }) => {
    API.runWaveSimulation(
      branchId,
      schema,
      (res) => {
        setSimulationSummariesPage(res);
        setFiltersConfigurationPage(null);
      },
      (err) => {
        console.error(err);
      }
    );
  };

  const handleReRunWaveSimulation = ({ id: simulationId, schema }) => {
    API.reRunWaveSimulation(
      simulationId,
      schema,
      (res) => {
        setSimulationSummariesPage(res);
        setFiltersConfigurationPage(null);
      },
      (err) => {
        console.error(err);
      }
    );
  };

  const handleOpenSimulationSummaries = ({ id: simulationId }) => {
    API.getWaveSimulation(
      simulationId,
      (res) => {
        setSimulationSummariesPage(res);
        setFiltersConfigurationPage(null);
      },
      () => {}
    );
  };

  const handleConfirmFilters = (simulationId, onSuccess) => {
    API.commitWaveSimulation(
      simulationId,
      () => {
        onSuccess();
        close();
        loadStagedImports();
        openSnackbar({
          message: translate('SIMULATION_COMMITTED_SUCCESSFULLY'),
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: translate('ERROR_COMMITTING_SIMULATION'),
          severity: 'error',
        });
      }
    );
  };

  return (
    <>
      <FiltersConfiguration
        show={Boolean(filtersConfigurationPage)}
        branchId={filtersConfigurationPage?.branchId}
        filterParameters={filtersConfigurationPage?.filterParameters}
        tabs={tabs}
        close={() => {
          setFiltersConfigurationPage(null);
          setSimulationSummariesPage(null);
        }}
        id={filtersConfigurationPage?.id}
        backToSummary={
          Boolean(simulationSummariesPage)
            ? () => {
                setFiltersConfigurationPage(null);
              }
            : undefined
        }
        onSubmit={
          filtersConfigurationPage?.id
            ? handleReRunWaveSimulation
            : handleRunWaveSimulation
        }
      />
      <SimulationSummaries
        data={simulationSummariesPage}
        summaries={simulationSummariesPage?.summaries}
        show={Boolean(simulationSummariesPage) && !filtersConfigurationPage}
        close={() => {
          setSimulationSummariesPage(null);
          setFiltersConfigurationPage(null);
        }}
        onClickChangeFilters={(data) => {
          setFiltersConfigurationPage(data);
        }}
        branchId={simulationSummariesPage?.branchId}
        tabs={tabs}
        reloadSummaries={handleOpenSimulationSummaries}
        handleConfirmFilters={handleConfirmFilters}
      />
      <Fade
        in={show && !filtersConfigurationPage && !simulationSummariesPage}
        unmountOnExit
      >
        <div
          style={{
            display: show ? 'block' : 'none',
            height: '100%',
          }}
        >
          <SectionPage
            hideExportExcel
            hideSorting
            hideFilters
            hideSearchBar
            fab={translate('NEW_SIMULATION')}
            fabVariant="extended"
            onClickFab={() => handleShowFiltersConfiguration({ id: null })}
            sectionName={`${translate('SIMULATIONS_HISTORY')} - ${
              capitalize(
                userBranches?.find(({ id }) => id === data?.branchId)
                  ?.branchDescription
              ) || ''
            }`}
            singlePageList={simulations}
            customClick={handleOpenSimulationSummaries}
            columns={[
              {
                id: 'simulationName',
                label: 'NAME',
              },
              {
                id: 'createdOn',
                label: 'CREATION_DATE',
                formatCallback: (value) =>
                  `${createNewDate(value).toLocaleDateString(culture, options)}`,
              },
            ]}
            customStatusChipCallback={({ data }) => null}
            sectionSubName={
              <BackButton label="STAGED_IMPORTS" onClick={close} />
            }
          />
        </div>
      </Fade>
    </>
  );
};

export default SimulationsHistory;
